<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import { activeNavItem, useCurrentApp } from '@/composables/useNavPrefs'
import { useUserScope } from '@/composables/useUserScope'
import { HOME_APP_ID, appItems, appLandingPath } from '@/nav/apps'
import { itemForPath, navLabel } from '@/nav/items'
import { useAuthStore } from '@/stores/auth'

/**
 * Breadcrumbs (§11): Home › App › screen › record. The trail is derived from
 * the nav declaration and the route's own title, so a new screen gets a
 * correct trail by existing rather than by remembering to register one.
 *
 * The app crumb is the app the shell resolved, not merely the first app that
 * lists the path, so if a screen is ever listed in two apps it shows the
 * trail the user actually followed (no screen is today). Home's own screens skip
 * the app crumb — "Home › Home › Approvals" says nothing twice.
 *
 * Nothing renders on `/`: the home screen is where every trail starts.
 */

const auth = useAuthStore()
const route = useRoute()
const { t } = useI18n()

const userScope = useUserScope()
const { current, onHomeScreen } = useCurrentApp(userScope, auth.can)

interface Crumb {
  label: string
  to?: string
}

const crumbs = computed<Crumb[]>(() => {
  const key = route.meta.titleKey
  const title = key === undefined ? '' : t(key)
  const trail: Crumb[] = [{ label: t('nav.home'), to: '/' }]

  const entry = current.value
  const inApp = entry === undefined ? undefined : activeNavItem(appItems(entry.part), route.path)
  const item = inApp ?? itemForPath(route.path)

  if (item === undefined) {
    // A screen no menu lists (an error page, an unlisted deep link): the
    // title is all there is to say, and naming an app would be a guess.
    if (title !== '') trail.push({ label: title })
    return trail
  }

  // Only name the app when the screen is in it: a route the shell could not
  // place keeps the previous app's sidebar, but must not claim its trail.
  if (inApp !== undefined && entry !== undefined && entry.app.id !== HOME_APP_ID) {
    trail.push({ label: t(entry.app.labelKey), to: appLandingPath(entry.part) })
  }

  const label = navLabel(item)
  if (route.path === item.to) {
    trail.push({ label })
    return trail
  }

  trail.push({ label, to: item.to })
  if (title !== '' && title !== label) trail.push({ label: title })
  return trail
})
</script>

<template>
  <nav v-if="!onHomeScreen" class="breadcrumbs" :aria-label="t('nav.breadcrumbLandmark')">
    <ol class="breadcrumbs__list">
      <li v-for="(crumb, index) in crumbs" :key="`${crumb.label}-${index}`" class="breadcrumbs__item">
        <AppIcon v-if="index > 0" name="chevronRight" :size="12" class="breadcrumbs__sep" />
        <RouterLink v-if="crumb.to && index < crumbs.length - 1" class="breadcrumbs__link" :to="crumb.to">
          {{ crumb.label }}
        </RouterLink>
        <span v-else class="breadcrumbs__current" aria-current="page">{{ crumb.label }}</span>
      </li>
    </ol>
  </nav>
</template>
